import React, { useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router";
import { Search, Loader2 } from "lucide-react";
import { compareEventsByNearestStart, compareEventsForListing } from "@mvk/shared";
import { useTranslation } from "react-i18next";
import { useAppStore } from "../store/useAppStore";
import { eventsService } from "../services/eventsService";
import { maxPlatform } from "../services/maxPlatform";
import { PageHeader } from "../components/PageHeader";
import { PageShell } from "../components/PageShell";
import { Chip } from "../components/Chip";
import { SearchInput } from "../components/SearchInput";
import { EmptyState } from "../components/EmptyState";
import { EventPosterCard } from "../components/EventPosterCard";
import { ClaimExhibitorEntryCard } from "../components/ClaimExhibitorEntryCard";
import type { Event } from "../types";

type EventFilter = "actual" | Event["status"] | "all";

export function EventsPage() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { selectEvent, completeOnboarding, selectedEventId } = useAppStore();
  const [filter, setFilter] = useState<EventFilter>("actual");
  const [search, setSearch] = useState("");
  const [allEvents, setAllEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [pastExpanded, setPastExpanded] = useState(false);
  const PAST_PREVIEW = 3;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        eventsService.clearCache();
        const list = await eventsService.getAll();
        if (!cancelled) setAllEvents(list);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [i18n.language]);

  const events = useMemo(() => {
    let list = [...allEvents];
    if (filter === "actual") {
      list = list.filter((e) => e.status !== "past");
    } else if (filter !== "all") {
      list = list.filter((e) => e.status === filter);
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(
        (e) =>
          e.title.toLowerCase().includes(q) ||
          e.industry.toLowerCase().includes(q) ||
          e.venueName.toLowerCase().includes(q) ||
          e.city.toLowerCase().includes(q),
      );
    }
    return list.sort(
      filter === "past" || filter === "all"
        ? compareEventsForListing
        : compareEventsByNearestStart,
    );
  }, [allEvents, filter, search]);

  const handleSelect = (event: Event) => {
    maxPlatform.hapticFeedback("medium");
    selectEvent(event.id);
    completeOnboarding();
    navigate("/home", { replace: true });
  };

  return (
    <>
      <PageHeader title={t("events.title")} subtitle={loading ? t("events.subtitle_loading") : t("events.subtitle", { n: events.length })} back />
      <PageShell>
        <div className="mt-3 space-y-3">
          <ClaimExhibitorEntryCard />
          {loading ? (
            <div className="flex justify-center py-16 text-gray-400">
              <Loader2 size={28} className="animate-spin" />
            </div>
          ) : (
            <>
              <SearchInput
                value={search}
                onChange={setSearch}
                placeholder={t("events.placeholder")}
              />

              <div
                className="flex items-center gap-2 overflow-x-auto pb-1 -mx-1 px-1"
                style={{ scrollbarWidth: "none" }}
              >
                <Chip
                  label={t("events.filter_actual")}
                  selected={filter === "actual"}
                  color="indigo"
                  onClick={() => setFilter("actual")}
                />
                <Chip
                  label={t("events.filter_active")}
                  selected={filter === "active"}
                  color="emerald"
                  onClick={() => setFilter("active")}
                />
                <Chip
                  label={t("events.filter_upcoming")}
                  selected={filter === "upcoming"}
                  color="indigo"
                  onClick={() => setFilter("upcoming")}
                />
                <Chip
                  label={t("events.filter_archive")}
                  selected={filter === "past"}
                  color="default"
                  onClick={() => setFilter("past")}
                />
                <Chip
                  label={t("events.filter_all")}
                  selected={filter === "all"}
                  color="default"
                  onClick={() => setFilter("all")}
                />
              </div>

              {events.length === 0 ? (
                <EmptyState
                  icon={<Search size={28} />}
                  title={t("events.empty_title")}
                  description={t("events.empty_desc")}
                />
              ) : (
                <div className="space-y-3 pb-4">
                  {(filter === "past" && !pastExpanded
                    ? events.slice(0, PAST_PREVIEW)
                    : events
                  ).map((event) => (
                    <EventPosterCard
                      key={event.id}
                      event={event}
                      onSelect={handleSelect}
                      current={event.id === selectedEventId}
                    />
                  ))}
                  {filter === "past" && events.length > PAST_PREVIEW && (
                    <button
                      type="button"
                      onClick={() => setPastExpanded((v) => !v)}
                      className="w-full rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-[13px] text-indigo-600"
                    >
                      {pastExpanded
                        ? t("onboarding.past_collapse")
                        : t("onboarding.past_expand", { n: events.length - PAST_PREVIEW })}
                    </button>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </PageShell>
    </>
  );
}
